import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/features/auth';
import { cn } from '@/lib/utils';
import { LoaderCircle, ArrowRight } from 'lucide-react';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Check if we came from an embed route (via redirect state) or if current path is embed
  const fromPath = (location.state as { from?: { pathname: string } })?.from?.pathname;
  const isEmbed = fromPath?.startsWith('/embed') || window.location.pathname.startsWith('/embed');
  const from = fromPath || (isEmbed ? '/embed/dids' : '/');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const success = await login(email, password);
    if (success) {
      navigate(from, { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-4 relative overflow-hidden">
      {/* Subtle radial gradients for depth */}
      <div
        className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.05) 0%, transparent 70%)',
        }}
      />
      <div
        className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(168, 85, 247, 0.05) 0%, transparent 70%)',
        }}
      />

      {/* Large watermark - perspective grid logo behind card */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{ zIndex: 0 }}
      >
        <img
          src="/logo-icon.png"
          alt=""
          className="w-[700px] h-[700px] object-contain"
          style={{
            opacity: 0.03,
            filter: 'grayscale(100%)',
          }}
        />
      </div>

      {/* Main content */}
      <div className="w-full max-w-md relative z-10">
        {/* Login Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <img
              src="/logo-black.png"
              alt="telviaIQ"
              className="h-10 w-auto mx-auto mb-6"
            />
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">
              Sign in to telviaIQ
            </h1>
            <p className="text-slate-500 text-sm font-medium mt-2">
              Access your carrier infrastructure.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block font-mono text-[10px] tracking-widest text-slate-400 uppercase mb-2"
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                required
                className={cn(
                  "w-full border border-slate-200 bg-slate-50/50 rounded-lg py-3 px-4",
                  "text-sm text-slate-900 placeholder:text-slate-400",
                  "transition-all duration-200",
                  "focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                )}
              />
            </div>

            {/* Password Field */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label
                  htmlFor="password"
                  className="block font-mono text-[10px] tracking-widest text-slate-400 uppercase"
                >
                  Password
                </label>
                <a
                  href="#"
                  className="text-indigo-600 font-semibold text-xs hover:text-indigo-700 transition-colors"
                >
                  Forgot?
                </a>
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
                className={cn(
                  "w-full border border-slate-200 bg-slate-50/50 rounded-lg py-3 px-4",
                  "text-sm text-slate-900 placeholder:text-slate-400",
                  "transition-all duration-200",
                  "focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                )}
              />
            </div>

            {/* Submit Button - Gradient CTA */}
            <button
              type="submit"
              disabled={isLoading}
              className={cn(
                "w-full py-3 rounded-lg font-bold text-white",
                "bg-gradient-to-r from-indigo-600 to-purple-600",
                "shadow-lg shadow-indigo-200",
                "transition-all duration-200",
                "hover:brightness-110",
                "active:scale-[0.98]",
                "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:brightness-100 disabled:active:scale-100",
                "flex items-center justify-center gap-2"
              )}
            >
              {isLoading ? (
                <LoaderCircle className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  Sign In
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </form>

          {/* SSO Option */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-3 text-slate-400 font-medium">or</span>
              </div>
            </div>
            <button
              type="button"
              className={cn(
                "w-full mt-4 py-3 rounded-lg font-medium text-slate-700",
                "border border-slate-200 bg-white",
                "transition-all duration-200",
                "hover:bg-slate-50 hover:border-slate-300",
                "active:scale-[0.98]",
                "flex items-center justify-center gap-2"
              )}
            >
              <svg className="h-4 w-4 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v8M8 12h8" />
              </svg>
              Sign in with SSO
            </button>
          </div>
        </div>

        {/* Contact Support */}
        <p className="text-center text-sm text-slate-500 mt-6">
          Need an account?{' '}
          <a href="mailto:support@speakiq.com" className="text-indigo-600 hover:text-indigo-700 font-medium">
            Contact Support
          </a>
        </p>
      </div>

      {/* Infrastructure Footer */}
      <div className="absolute bottom-6 left-0 right-0 text-center">
        <p className="font-mono text-[10px] text-slate-400 tracking-[0.2em]">
          SYSTEM STATUS: <span className="text-emerald-500">OPERATIONAL</span> // SECURE AUTHENTICATION REQUIRED
        </p>
      </div>
    </div>
  );
}
